// IFC export times and the order of versions (docs/timber-panel-tipos-proposal.md
// §9.6). The server reads the export time from each IFC header and warns, never
// blocks, when a newer version of a file, or the model about to revise a design,
// was exported before the one it replaces. These helpers read those warnings
// and format their times; the screens only translate and draw them.

function pad(n) {
    return String(n).padStart(2, "0")
}

/** A Date from an ISO text, or null. A time with no zone is UTC, as the
 * server reads it (and as `Timestamp` does). */
export function parseStamp(iso) {
    if (iso instanceof Date) return isNaN(iso) ? null : iso
    if (!iso || typeof iso != "string") return null
    const zoned = /(Z|[+-]\d\d:?\d\d)$/i.test(iso)
    // A date alone has no time to place in a zone.
    const date_only = !iso.includes("T") && !iso.includes(" ")
    const text = zoned || date_only ? iso : iso + "Z"
    const d = new Date(text)
    return isNaN(d) ? null : d
}

/** `2026-10-30 09:15` in the reader's time, the way `Timestamp` writes every
 * date and time of the app, or "" when the time is unknown. */
export function formatStamp(iso) {
    const d = parseStamp(iso)
    if (!d) return ""
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** The file's warning, `{exported, other_version, other_exported}`, or null
 * when it has none or the server sent something unreadable. */
export function olderExport(file) {
    const w = file?.meta?.older_export
    if (!w || typeof w != "object") return null
    if (!parseStamp(w.exported) || !parseStamp(w.other_exported)) return null
    return { exported: w.exported, other_version: w.other_version ?? null, other_exported: w.other_exported }
}

/** The export time of a design's model, or "" (revisions made before the
 * export time was kept, and models with no time in their header). */
export function modelExportedAt(design) {
    const v = design?.meta?.model_exported_at
    return parseStamp(v) ? v : ""
}

/** A revised design's `older_model`, `{current_exported, new_exported}`, or null. */
export function olderModel(entry) {
    const w = entry?.older_model
    if (!w || typeof w != "object") return null
    if (!parseStamp(w.current_exported) || !parseStamp(w.new_exported)) return null
    return { current_exported: w.current_exported, new_exported: w.new_exported }
}

/** The designs the Update designs preview will revise from an older export:
 * `[{name, level, current_exported, new_exported}]`. */
export function olderModelRevised(preview) {
    return (preview?.designs?.revised || [])
        .map(r => {
            const w = olderModel(r)
            return w ? { name: r.name ?? "", level: r.level ?? null, ...w } : null
        })
        .filter(Boolean)
}

/** The designs a finished run revised from an older export, from its result. */
export function olderModelDesigns(result) {
    const list = result?.older_model_designs
    if (!Array.isArray(list)) return []
    return list
        .filter(d => d && typeof d == "object")
        .map(d => ({
            name: d.name ?? "",
            level: d.level ?? null,
            current_exported: d.current_exported ?? "",
            new_exported: d.new_exported ?? "",
        }))
}

/** How many designs a `design_update_done` notification says were revised
 * from an older export: a count, or a list of names or designs. 0 when the
 * notification does not say (older backends). */
export function olderModelCount(params) {
    const p = params || {}
    for (const key of ["older_models", "older_model_designs", "older_model_count"]) {
        const v = p[key]
        if (Array.isArray(v)) return v.length
        const n = Number(v)
        if (v !== null && v !== undefined && v !== "" && Number.isFinite(n) && n > 0) return n
    }
    const names = olderModelNames(p)
    return names.length
}

/** The names of those designs, when the notification carries them in its params. */
export function olderModelNames(params) {
    const p = params || {}
    for (const key of ["older_model_names", "older_model_designs", "older_models"]) {
        const v = p[key]
        if (Array.isArray(v))
            return v
                .map(x => (x && typeof x == "object" ? x.name : x))
                .filter(x => x !== null && x !== undefined && x !== "")
                .map(String)
        if (key == "older_model_names" && typeof v == "string" && v.trim())
            return v.split(",").map(s => s.trim()).filter(Boolean)
    }
    return []
}

/**
 * A translated text cut into pieces at its `{placeholders}`, so the screen can
 * draw the filled ones in Roboto Mono: `[{text, mono}]`. A placeholder with no
 * value stays as written; one listed in `plain` is filled but not mono.
 */
export function textSegments(template, params = {}, plain = []) {
    const out = []
    const re = /\{(\w+)\}/g
    const text = String(template ?? "")
    let last = 0
    let m
    while ((m = re.exec(text))) {
        if (m.index > last) out.push({ text: text.slice(last, m.index), mono: false })
        const v = params[m[1]]
        if (v === undefined || v === null) out.push({ text: m[0], mono: false })
        else out.push({ text: String(v), mono: !plain.includes(m[1]) })
        last = re.lastIndex
    }
    if (last < text.length) out.push({ text: text.slice(last), mono: false })
    return out
}

/** The same text, filled, for a `title` or anywhere one string is needed. */
export function segmentsText(segments) {
    return segments.map(s => s.text).join("")
}

/** The params of a file's warning text: `{exported}`, `{version}`, `{other}`. */
export function olderExportParams(w) {
    return {
        exported: formatStamp(w.exported),
        version: w.other_version ?? "?",
        other: formatStamp(w.other_exported),
    }
}

/** The params of a design's warning text: `{name}`, `{new}`, `{current}`. */
export function olderModelParams(d) {
    return {
        name: d.name,
        new: formatStamp(d.new_exported) || "—",
        current: formatStamp(d.current_exported) || "—",
    }
}
